/**
 * /verify-email?token=... — invoked when the user clicks the verify link
 * in their inbox. Calls the backend, refreshes the auth context, and
 * routes them to their dashboard.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, XCircle, Mail } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { refresh } = useAuth();
  const [state, setState] = useState({ loading: true, ok: false, message: "" });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, message: "Missing verification token." });
      return;
    }
    http.post("/auth/verify-email", { token })
      .then((r) => {
        setState({ loading: false, ok: true, message: r.data.email });
        refresh?.();
      })
      .catch((e) => {
        setState({
          loading: false,
          ok: false,
          message: e?.response?.data?.detail || "This verification link is invalid.",
        });
      });
  }, [token, refresh]);

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="verify-email-page">
      <div className="mb-6 flex justify-center">
        {state.loading ? (
          <Mail className="w-14 h-14" style={{ color: "var(--wofy-primary)" }} />
        ) : state.ok ? (
          <CheckCircle2 className="w-14 h-14" style={{ color: "#2C7A4B" }} />
        ) : (
          <XCircle className="w-14 h-14" style={{ color: "#B23A3A" }} />
        )}
      </div>
      <div className="overline mb-2">EMAIL VERIFICATION</div>
      {state.loading ? (
        <h1 className="font-display text-3xl">Verifying your email…</h1>
      ) : state.ok ? (
        <>
          <h1 className="font-display text-3xl">You're all set!</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            <b>{state.message}</b> is now verified. You can post causes, create family trees, and share your story.
          </p>
          <Link to="/dashboard" data-testid="verify-email-dashboard" className="wofy-btn-primary mt-8 inline-flex">
            Open your dashboard
          </Link>
        </>
      ) : (
        <>
          <h1 className="font-display text-3xl">Verification failed</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{state.message}</p>
          <Link to="/register" data-testid="verify-email-register" className="wofy-btn-primary mt-8 inline-flex">
            Back to sign up
          </Link>
        </>
      )}
    </div>
  );
}
